/**
 * GIS Services data shapes.
 *
 * The app loads the WFS/WMS service layers (across products and
 * versions) and arranges them into a nested catalog the UI walks
 * top-down: product → version → module → layer. A flat `layerById` index lets
 * the layer-detail route resolve a layer in O(1) from a URL param.
 *
 * Unlike the other product apps, GIS Services is a *catalog* of what the
 * other apps surface — its product/module enums must stay in lockstep with
 * the source-of-truth definitions in `apps/safesystem/safecurves` and `apps/mire/ableymire`.
 * We therefore re-export those enums here rather than redeclaring them.
 * This is the one place where one app reads another app's interfaces; the
 * sibling apps still do not reference each other.
 */

import {
	ESafeCurvesModule,
	EProduct as ESafeCurvesProduct
} from '#lib/apps/safesystem/safecurves/interfaces.js';
import {
	ESafeIntersectionsModule,
	EProduct as ESafeIntersectionsProduct
} from '#lib/apps/safesystem/safeintersections/interfaces.js';
import {
	EAbleyLinksModule,
	EProduct as EAbleyLinksProduct
} from '#lib/apps/links/ableylinks/interfaces.js';
import { EMireModule, EProduct as EMireProduct } from '#lib/apps/mire/ableymire/interfaces.js';
import { EProduct as ENswRlgspProduct } from '#lib/apps/speedmanagement/nsw-rlgsp/interfaces.js';
import {
	moduleTable as nswRlgspModuleTable,
	type NswRlgspModule
} from '#lib/apps/speedmanagement/nsw-rlgsp/layerConfig.js';

/**
 * Products surfaced in this app today. Built from each source app's `EProduct`
 * so the string values stay aligned; not an enum because the upstream
 * `EProduct` names collide.
 */
export const EGisProduct = {
	SafeCurves: ESafeCurvesProduct.SafeCurves,
	SafeIntersections: ESafeIntersectionsProduct.SafeIntersections,
	AbleyLinks: EAbleyLinksProduct.AbleyLinks,
	AbleyMire: EMireProduct.MIRE,
	SpeedManagement: ENswRlgspProduct.NswRlgsp
} as const;
export type EGisProduct = (typeof EGisProduct)[keyof typeof EGisProduct];

/**
 * Module-id union — the rest of GIS Services treats modules opaquely.
 *
 * Template-literal types (`` `${Enum}` ``) yield the union of each enum's
 * string *values* rather than its enum literal types. That matters because
 * SafeCurves and SafeIntersections both declare 'Identify'/'Prioritize'
 * members: a plain enum union gives a `Record<EGisModule, …>` with colliding
 * keys that TypeScript refuses to index. The value union dedupes them, and
 * enum members stay assignable to their value literals.
 */
export type EGisModule =
	| `${ESafeCurvesModule}`
	| `${ESafeIntersectionsModule}`
	| `${EAbleyLinksModule}`
	| `${EMireModule}`
	| NswRlgspModule;

/** Display order for the modules under each product. */
export const productModulesOrdered: Record<EGisProduct, EGisModule[]> = {
	[EGisProduct.SafeCurves]: Object.values(ESafeCurvesModule),
	[EGisProduct.SafeIntersections]: Object.values(ESafeIntersectionsModule),
	[EGisProduct.AbleyLinks]: Object.values(EAbleyLinksModule),
	[EGisProduct.AbleyMire]: Object.values(EMireModule),
	[EGisProduct.SpeedManagement]: Object.keys(nswRlgspModuleTable) as EGisModule[]
};

// Capabilities is empty when the layer doesn't publish that service.
type TGisServiceLink = { layer: { template: string }; capabilities: string };

/** A catalog layer as `buildCatalog` assembles it from the WFS/WMS lists and its operational layer. */
export interface IGisFeature {
	id: string;
	title: string;
	abstract: string;
	keywords: Record<string, string>;
	attribution: string;
	nativeCrs: string;
	geometryType: 'point' | 'line' | 'polygon' | null;
	defaultStyle: string;
	additionalStyles: string[];
	featureCount: number | null;
	latLonBbox: { minx: number; miny: number; maxx: number; maxy: number };
	nativeBbox: { minx: number; miny: number; maxx: number; maxy: number };
	/** Preview-map tiles only, not an offered service. */
	xyzTemplate: string;
	hasWfs: boolean;
	hasWms: boolean;
	services: {
		wms: TGisServiceLink;
		wfs: TGisServiceLink;
	};
}

/** An operational feature carrying its parsed product + module annotations. */
export interface IGisLayer extends IGisFeature {
	product: EGisProduct;
	module: EGisModule;
}

/** `module → IGisLayer[]` for a single product version. */
type TGisModuleRecord = Partial<Record<EGisModule, IGisLayer[]>>;
/** `version → moduleRecord`. */
export type TGisVersionRecord = Record<string, TGisModuleRecord>;
/** `product → versionRecord`. */
export type TGisProductRecord = Partial<Record<EGisProduct, TGisVersionRecord>>;

/**
 * The shape `loaders.system` returns. Held in `geodataStore.systemData` and
 * narrowed at the call site (the `TSystem` parameter for the GIS Services
 * AppConfig).
 */
export interface IGisServicesSystemData {
	/** Nested catalog walked by `ProductCatalog.svelte`. */
	products: TGisProductRecord;
	/** Flat lookup by `feature.id` for layer-detail routes. */
	layerById: Record<string, IGisLayer>;
	/** Stable product display order (subset of EGisProduct that has data). */
	productOrder: EGisProduct[];
}

/** A `{label,value}` pair used by the per-service format dropdowns. */
export type TLayerFormat = { label: string; value: string };

/** Service kinds offered on the layer-detail page. */
export type TServiceKind = 'wms' | 'wfs';
